import React, { useEffect, useMemo, useState } from 'react';

import {
  Alignments,
  Avatar,
  Button,
  Menu,
  Pecten,
  Popover,
  Sizes,
  Text,
} from '@sede-enterprise/shell-ds-react-framework';

import { Outlet, useLocation, useNavigate } from 'react-router';

import { sendNotification } from 'src/UI/Notification';
import { themeActions } from 'src/store/Theme';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { IFullState } from 'src/types/All.type';
import UserOptions from '../../Auth/UserOptions';
import IconExport from '../Icons/IconExport';
import {
  EOS_APPLICATION_NAME,
  EOS_APPLICATION_SUBTITLE,
  EOS_ERROR_MESSAGES,
  EOS_MENU_TABS,
} from '../constants/Layout.constants';
import { eosPermissions } from '../constants/Permissions';
import { useEosToken } from '../services/EosTokenService';
import { getSelectedPortfolioId } from '../store/slices/dashboard';
import {
  fetchEOSUserPermissions,
  selectEOSPermissions,
  selectEOSUser,
} from '../store/slices/eosUser';
import { fetchReportTypesForSupplyContract } from '../store/slices/reportTypes';
import { EOSLayoutState } from '../store/types/Layout.types';
import {
  AvatarArea,
  EOSAdminButton,
  EOSContentContainer,
  EOSHeader,
  EOSLayoutContainer,
  EOSMidSection,
  EOSSwitchAppButton,
  EOSUserRole,
  MainContainer,
  NavBarStyled,
  PectenWrapper,
} from './style';

const EOSLayout: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { isDark } = useAppSelector((state: IFullState) => state.Theme);
  const { userName, mail, isAuthorized } = useAppSelector(
    (state: IFullState) => state.genesisUserRolePermission,
  );
  const { isNewFilePresent } = useAppSelector(
    (state: IFullState) => state.forecastAnalysis.forecastFiles,
  );
  const eosUserPermissions = useAppSelector(selectEOSPermissions);
  const { user: eosUser } = useAppSelector(selectEOSUser);
  const { isTokenReady } = useEosToken();
  const selectedPortfolioId = useAppSelector(getSelectedPortfolioId);
  const { lastFetchedPortfolioId } = useAppSelector((state) => state.reportTypes);

  const [layoutState, setLayoutState] = useState<EOSLayoutState>({
    currentTab: location.pathname.split('/')[2] || 'dashboard',
    notifications: [],
    notificationCount: 0,
    openDrawer: false,
  });

  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const currentTab = pathSegments[2] || 'dashboard';
    setLayoutState((prev) => ({
      ...prev,
      currentTab,
    }));
  }, [location]);

  useEffect(() => {
    if (isTokenReady) {
      dispatch(fetchEOSUserPermissions());
    }
  }, [dispatch, isTokenReady]);

  useEffect(() => {
    if (isTokenReady && selectedPortfolioId && selectedPortfolioId !== lastFetchedPortfolioId) {
      dispatch(fetchReportTypesForSupplyContract(selectedPortfolioId));
    }
  }, [dispatch, isTokenReady, lastFetchedPortfolioId, selectedPortfolioId]);

  const handleThemeChange = (value: boolean) => {
    dispatch(
      themeActions.updateTheme({
        isDark: value,
      }),
    );
  };

  const handleTabChange = (value: string) => {
    if (layoutState.currentTab === value) {
      return;
    }

    const tab = EOS_MENU_TABS.find((item) => item.key === value);

    if (tab) {
      setLayoutState((prev) => ({ ...prev, currentTab: value }));
      navigate(tab.route);
    } else {
      sendNotification({
        type: 'error',
        message: EOS_ERROR_MESSAGES.NAVIGATION_ERROR,
      });
    }
  };

  const handleBackToLanding = () => {
    navigate(isAuthorized ? '/' : '/eos/dashboard');
  };

  const excludedMenuTabs = ['dashboard', 'mapping-config'];

  const menuTabs = useMemo(() => {
    const tabPermissionMap: Record<string, string> = {
      'scenario-analysis': eosPermissions.viewForecast,
      forecast: eosPermissions.viewForecast,
      audit: eosPermissions.viewAuditLog,
    };

    return EOS_MENU_TABS.filter((tab) => {
      const required = tabPermissionMap[tab.key];
      return !required || eosUserPermissions.includes(required);
    }).map((tab) => {
      if (tab.key !== 'forecast' || !isNewFilePresent) {
        return tab;
      }

      return {
        ...tab,
        label: (
          <span className='eos-menu-label-with-dot'>
            {tab.label}
            <span className='eos-forecast-dot' aria-label='New forecast file indicator' />
          </span>
        ),
      };
    });
  }, [isNewFilePresent, eosUserPermissions]);

  return (
    <EOSLayoutContainer id='eos-layout-container'>
      <MainContainer>
        <NavBarStyled
          data-testid='eos-navbar'
          pectenArea={
            <PectenWrapper>
              <Pecten
                padding='minimum'
                size={Sizes.Medium}
                aria-label='Pecten Logo'
                title='Pecten Logo'
              />
              <EOSHeader flexDirection='column'>
                <Text size='small'>{EOS_APPLICATION_NAME}</Text>
                <Text>
                  <strong>{EOS_APPLICATION_SUBTITLE}</strong>
                </Text>
              </EOSHeader>
            </PectenWrapper>
          }
          elevation={false}
          itemsAlignment={Alignments.Center}
          avatarArea={
            <AvatarArea>
              {/* <EnvironmentBanner /> */}

              <Button
                size='medium'
                variant='transparent'
                iconOnly
                onClick={() => handleThemeChange(!isDark)}
                icon={isDark ? <IconExport.SunSolid /> : <IconExport.MoonSolid />}
              />

              {eosUserPermissions.includes(eosPermissions.viewRolesAndManagement) && (
                <EOSAdminButton
                  type='button'
                  aria-label='Roles & Management'
                  title='Roles & Management'
                  data-testid='eos-roles-management'
                  onClick={() => navigate('/eos/roles-and-management')}
                >
                  <IconExport.PersonGearSolid />
                </EOSAdminButton>
              )}
              <Popover arrow popup={<UserOptions />} popupPlacement='bottomRight'>
                <div className='pointer'>
                  {eosUser?.role?.roleName && (
                    <EOSUserRole data-testid='eos-user-role' className='eos-user-role'>
                      {eosUser.role.roleName}
                    </EOSUserRole>
                  )}
                  <Avatar
                    size={Sizes.Medium}
                    label={userName}
                    secondaryLabel={mail}
                    aria-label={userName}
                  />
                </div>
              </Popover>
            </AvatarArea>
          }
        >
          <div className='eos-center-component'>
            {isAuthorized && (
              <EOSSwitchAppButton>
                <Button
                  variant='transparent'
                  onClick={handleBackToLanding}
                  icon={<IconExport.HomeSolid />}
                  size='large'
                  iconOnly
                  className='eos-switch-button'
                  title='Switch Application'
                />
              </EOSSwitchAppButton>
            )}
            {excludedMenuTabs.includes(location.pathname.split('/')[2]) === false && (
              <Menu
                mode='horizontal'
                emphasis
                items={menuTabs}
                activeKey={layoutState.currentTab}
                size={Sizes.Large}
                selectedKeys={[layoutState.currentTab]}
                onSelect={(selected) => handleTabChange(selected.key)}
                className='eos-left-menu'
              />
            )}
          </div>
        </NavBarStyled>
      </MainContainer>

      <EOSMidSection>
        <EOSContentContainer>
          <Outlet />
        </EOSContentContainer>
      </EOSMidSection>
    </EOSLayoutContainer>
  );
};

export default EOSLayout;
